import Mousetrap from 'mousetrap';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {visitorEnriched, type AstView, type EnrichedAstNode} from 'rendition-core';

import {useHighlighterContext, HighlighterState, HighlightType} from 'Contexts/HighlighterContext';

interface SubHookArgs {
    view: AstView;
    setActive: (val: boolean) => void;
    active: boolean;
    highlightType: HighlightType;
    nodesToHighlight: EnrichedAstNode[];
    conditional: (node) => boolean;
    addNodeHighlight: HighlighterState['addNodeHighlight'];
    clearHighlightByType: HighlighterState['clearHighlightByType'];
    recalculateHighlights: HighlighterState['recalculateHighlights'];
    removeNodeHighlight: HighlighterState['removeNodeHighlight'];
}

const useToggleHighlights = ({
    view,
    active,
    highlightType,
    conditional,
    setActive,
    addNodeHighlight,
    clearHighlightByType,
    recalculateHighlights,
    removeNodeHighlight,
    nodesToHighlight,
}: SubHookArgs) => {
    const toggleHighlights = useCallback(() => {
        if (active) {
            clearHighlightByType(highlightType);
            setActive(false);
        } else {
            setActive(true);
        }
    }, [active, clearHighlightByType, highlightType, setActive]);

    useEffect(() => {
        if (active) {
            nodesToHighlight.forEach((node) => {
                visitorEnriched(node, (n) => {
                    if (conditional(n)) {
                        addNodeHighlight(n.internalId, highlightType);
                    }
                });
            });
            recalculateHighlights();
        } else {
            clearHighlightByType(highlightType);
        }

        return () => {
            nodesToHighlight.forEach((node) => {
                visitorEnriched(node, (n) => {
                    removeNodeHighlight(n.internalId, highlightType);
                });
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, highlightType, nodesToHighlight]);

    return toggleHighlights;
};

const useHotkeys = (view: AstView, selectedNodes: EnrichedAstNode[]) => {
    const nodesToHighlight = useMemo(
        () => (selectedNodes.length ? selectedNodes : [view.tree]),
        [selectedNodes, view.tree]
    );
    const [showingWidths, setShowingWidths] = useState(false);
    const [showPaddingHighlight, setShowPaddingHighlight] = useState(false);
    const [showMarginHighlight, setShowMarginHighlight] = useState(false);
    const {addNodeHighlight, clearHighlightByType, recalculateHighlights, setShowGroupHighlight, removeNodeHighlight} =
        useHighlighterContext();

    const toggleGroupHighlight = useCallback(() => setShowGroupHighlight((s) => !s), [setShowGroupHighlight]);

    const toggleWidths = useToggleHighlights({
        view,
        highlightType: 'Width',
        conditional: (n) => n?.calculatedLayoutAbstraction.widthSpacingStrategy !== 'hug',
        active: showingWidths,
        setActive: setShowingWidths,
        addNodeHighlight,
        clearHighlightByType,
        recalculateHighlights,
        nodesToHighlight,
        removeNodeHighlight,
    });

    const togglePadding = useToggleHighlights({
        view,
        highlightType: 'Padding',
        conditional: () => true,
        active: showPaddingHighlight,
        setActive: setShowPaddingHighlight,
        addNodeHighlight,
        clearHighlightByType,
        recalculateHighlights,
        nodesToHighlight,
        removeNodeHighlight,
    });

    const toggleMargin = useToggleHighlights({
        view,
        highlightType: 'Margin',
        conditional: () => true,
        active: showMarginHighlight,
        setActive: setShowMarginHighlight,
        addNodeHighlight,
        clearHighlightByType,
        recalculateHighlights,
        nodesToHighlight,
        removeNodeHighlight,
    });

    useEffect(() => {
        Mousetrap.bind('w', toggleWidths);
    }, [toggleWidths]);

    useEffect(() => {
        Mousetrap.bind('p', togglePadding);
    }, [togglePadding]);

    useEffect(() => {
        Mousetrap.bind('m', toggleMargin);
    }, [toggleMargin]);

    useEffect(() => {
        Mousetrap.bind('g', toggleGroupHighlight);
    }, [toggleGroupHighlight]);
};

export default useHotkeys;
